import { useMemo, type ReactNode } from 'react';
import { DndContext, DragOverlay } from '@dnd-kit/core';
import { addDays, startOfDay, startOfWeek, format, getDaysRemainingInWeek } from '~/util/date';
import { DayCell } from './DayCell';
import { combineDateWithTimeOfDay, useCalendarDnd } from './useCalendarDnd';
import type { CalendarEventModel, CellRenderer } from '../calendar/types';
import { Calendar, Loader2 } from 'lucide-react';
import type { Day } from 'date-fns';
import { EventChip } from './EventChip';

const WEEKDAY_LABELS = ['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'];

export interface MultiMonthCalendarProps<T extends CalendarEventModel> {
	events: T[];
	startDate: Date;
	endDate: Date;
	today?: Date;
	label?: string;
	onCalendarItemClicked?: (event: T, target: Element) => void;
	onCalendarCellClicked?: (date: Date) => void;
	onDragStopped?: (eventId: string, newStartTime: Date) => void;
	isDragAndDropValid?: (eventId: string, newStartTime: Date) => boolean;
	allowDragAndDrop?: boolean;
	renderCellContent?: CellRenderer;
	renderEventChip?: (event: T) => ReactNode;
	includeEpisodeLabels?: boolean;
	renderDayLabel?: (date: Date) => ReactNode;
	weekStartOverride?: Day;
	isLoading?: boolean;
	groupBy?: (event: T) => string;
}

function formatDateRange(start: Date, end: Date) {
	return `${format(start, 'MMMM yyyy')} — ${format(end, 'MMMM yyyy')}`;
}

function computeNumberOfWeeks(startDate: Date, weekStartsOn: Day): number {
	return getDaysRemainingInWeek(weekStartsOn, startDate) >= 4 ? 9 : 10;
}

function toIsoDate(date: Date) {
	const y = date.getFullYear();
	const m = String(date.getMonth() + 1).padStart(2, '0');
	const d = String(date.getDate()).padStart(2, '0');
	return `${y}-${m}-${d}`;
}

export function MultiMonthCalendar<T extends CalendarEventModel>({
	events,
	startDate,
	endDate,
	today = new Date(),
	label,
	onCalendarItemClicked,
	onCalendarCellClicked,
	onDragStopped,
	isDragAndDropValid,
	allowDragAndDrop = false,
	renderCellContent,
	renderEventChip,
	renderDayLabel,
	weekStartOverride,
	isLoading = false,
	groupBy
}: MultiMonthCalendarProps<T>) {
	const { sensors, activeEvent, handleDragStart, handleDragEnd, handleDragCancel } = useCalendarDnd({
		events,
		onDragStopped,
		isDragAndDropValid
	});

	const weekStart = weekStartOverride ?? (0 as Day);
	const weeks = computeNumberOfWeeks(startDate, weekStart);
	const totalCells = weeks * 7;

	const firstCellDate = useMemo(() => startOfWeek(startDate, { weekStartsOn: weekStart }), [startDate, weekStart]);

	const cellDates = useMemo(() => {
		const dates: Date[] = [];
		for (let i = 0; i < totalCells; i++) {
			dates.push(addDays(firstCellDate, i));
		}
		return dates;
	}, [firstCellDate, totalCells]);

	const eventsByDay = useMemo(() => {
		const map = new Map<string, T[]>();
		for (const event of events) {
			const key = toIsoDate(startOfDay(event.startTime));
			const bucket = map.get(key);
			if (bucket) bucket.push(event);
			else map.set(key, [event]);
		}
		return map;
	}, [events]);

	const rangeStart = startOfDay(startDate).getTime();
	const rangeEnd = startOfDay(endDate).getTime();

	const weekdayLabels = useMemo(() => {
		const rotated: string[] = [];
		for (let i = 0; i < 7; i++) {
			rotated.push(WEEKDAY_LABELS[(weekStart + i) % 7]!);
		}
		return rotated;
	}, [weekStart]);

	return (
		<DndContext sensors={sensors} onDragStart={handleDragStart} onDragEnd={handleDragEnd} onDragCancel={handleDragCancel}>
			<div className="relative grid w-full grid-cols-1 gap-2">
				<div className="mb-2 flex items-center justify-between px-1">
					<div className="flex items-center gap-2 text-sm font-medium text-gray-900">
						<Calendar className="size-4 text-gray-500" aria-hidden="true" />
						<span>{formatDateRange(startDate, endDate)}</span>
					</div>
					{label && <div className="text-xs text-gray-500">{label}</div>}
				</div>

				<div className="overflow-hidden rounded-md border border-gray-200">
					<div className="grid grid-cols-7 border-b border-gray-200 bg-gray-50">
						{weekdayLabels.map(label => (
							<div key={label} className="px-3 py-2 text-center text-xs font-medium text-gray-500">
								{label}
							</div>
						))}
					</div>

					<div className="grid grid-cols-7 border-l border-t-0 border-gray-200">
						{cellDates.map(date => {
							const cellTime = startOfDay(date).getTime();
							const isOutOfRange = cellTime < rangeStart || cellTime > rangeEnd;
							const key = toIsoDate(date);
							const dayEvents = eventsByDay.get(key) ?? [];
							const groupedEvents = groupBy
								? dayEvents.reduce((acc, event) => {
										const groupKey = groupBy(event);
										if (!acc[groupKey]) acc[groupKey] = [];
										acc[groupKey].push(event);
										return acc;
								  }, {} as Record<string, T[]>)
								: { '': dayEvents };
							return (
								<DayCell
									key={key}
									date={date}
									isOutOfRange={isOutOfRange}
									today={today}
									onCellClick={onCalendarCellClicked}
									renderCellContent={renderCellContent}
									droppable={allowDragAndDrop}
									isValidDropTarget={
										activeEvent && isDragAndDropValid && !isOutOfRange
											? isDragAndDropValid(activeEvent.id, combineDateWithTimeOfDay(date, activeEvent.startTime))
											: undefined
									}
									renderDayLabel={renderDayLabel}
									eventLayout="stack"
								>
									{dayEvents.map(event => (
										<EventChip
											key={event.id}
											event={event}
											onClick={onCalendarItemClicked}
											renderContent={renderEventChip}
											draggable={allowDragAndDrop}
											className="flex-row items-center justify-between"
											block
										/>
									))}
								</DayCell>
							);
						})}
					</div>
				</div>

				{isLoading && (
					<div className="absolute inset-0 z-10 flex items-center justify-center bg-white/70">
						<div className="flex size-12 items-center justify-center rounded-full bg-primary-100">
							<Loader2 className="size-6 animate-spin text-primary-600" />
						</div>
					</div>
				)}
			</div>

			<DragOverlay>{activeEvent ? <EventChip event={activeEvent} renderContent={renderEventChip} isOverlay /> : null}</DragOverlay>
		</DndContext>
	);
}
